// moduleCalendarBridge.js — capability-scoped NATIVE calendar access for
// workspace MODULES (manifest v2). The calendar-plane sibling of
// moduleDataBridge / moduleAiBridge / moduleFileBridge: when a module ref
// carries `capabilities.calendar`, the loader injects `moduleCalendarCreate`,
// `moduleCalendarUpdate`, `moduleCalendarRemove` and `moduleCalendarList` so a
// module can put a real event on the WORKSPACE calendar — and keep it in step —
// via `el.call`, never raw fetch (the publish-time module audit forbids that).
//
// Why this exists: a module that keeps its own event rows in the records plane
// has a calendar nobody else can see. Chaining through the workspace calendar
// service (`sdk.calendar.*` → /core/calendar/*) means the module's sessions
// show up in the native Calendar app, in freebusy, and in reminders — one
// source of truth instead of two.
//
// Every function returns a plain result object (`{ ok, ... } | { ok:false,
// error }`) and NEVER throws, so a module's scheduling UI degrades gracefully
// when the grant is absent or the service is still booting.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

// Services init ASYNC — `sdk.calendar` is undefined at runtime on some builds,
// so always prefer getService() (which returns a promise). Same trap the AI
// bridge documents.
const _calendarService = async (sdk) => {
  if (!sdk) return null
  try {
    return typeof sdk.getService === 'function' ? await sdk.getService('calendar') : sdk.calendar
  } catch (_) {
    return sdk.calendar || null
  }
}

// meetingMode — the ONE native room-creation path, shared verbatim with the
// calendar app's own New Booking (NewBookingModal.js "Symbols Meet"): the
// bridge el.calls meet's `createRoom` (kind 'scheduled', keyed by
// calendarEventId, transcribe on) and `meetRoomUrl` (the org+workspace-
// prefixed /meet#room= link), then stamps that link on the event
// description — the exact signal EventDetailModal / eventHasMeetLink read to
// recognise a call event. No second implementation lives here; a module
// that passes `meetingMode: 'symbols-meet'` gets the same event+room pair the
// calendar's booking form produces (MEET-FEATURES-BUILD-1 item 6, DECISIONS
// 2026-08-21 option A).
export const MEETING_MODE_SYMBOLS_MEET = 'symbols-meet'

const _meetLinkFor = (root, roomId, call) => {
  try {
    const url = call ? call('meetRoomUrl', root, roomId) : ''
    return typeof url === 'string' ? url : ''
  } catch (_) {
    return ''
  }
}

export const buildModuleCalendarBridge = (root, capabilities = {}, { getSdkFn = shellGetSDK, callFn } = {}) => {
  const cap = (capabilities && capabilities.calendar) || null
  // Idempotency memo — a module retry with the SAME `ref` (its own mirror
  // row id) in one session returns the triple it already minted instead of
  // a second event + second room. Keyed by ref only when the caller gave one.
  const _byRef = new Map()
  const wsId = () =>
    (root && root.workspace && (root.workspace.id || root.workspace._id)) || undefined
  const orgId = () =>
    root?.jwtClaims?.workspace_org_id || (root?.org && (root.org.id || root.org._id)) || undefined

  // Create a real workspace calendar event.
  //   moduleCalendarCreate({ title, start, end?, allDay?, location?, notes? })
  // `start` accepts a Date or any ISO-8601 string. `end` defaults to start +
  // 1h, because the native event model wants a range and a module that only
  // knows "a session happens that day" should not have to invent one.
  //   moduleCalendarCreate({ title, start, end?, allDay?, location?, notes?,
  //                          ref?, meetingMode? })
  // `meetingMode: 'symbols-meet'` also creates the native meet room and
  // returns `{ ok, id, event, roomId, joinUrl }`; any other value (or none)
  // creates the event alone (`roomId`/`joinUrl` null). When the event lands
  // but the room step fails, the result is still ok (the event exists) with
  // `meetError` naming why — the module decides whether to retry.
  const moduleCalendarCreate = async function (input = {}) {
    const ev = input || {}
    const title = typeof ev.title === 'string' ? ev.title.trim() : ''
    if (!title) return { ok: false, error: 'An event title is required.' }
    if (!ev.start) return { ok: false, error: 'An event start is required.' }
    const wantsMeet = ev.meetingMode === MEETING_MODE_SYMBOLS_MEET
    const refKey = ev.ref != null && String(ev.ref) !== '' ? String(ev.ref) : ''
    if (refKey && _byRef.has(refKey)) {
      // Retry semantics: the event never lands twice. If the first call
      // landed the event but the room step failed, the retry only redoes
      // the room step on the SAME event id.
      const prev = _byRef.get(refKey)
      if (wantsMeet && prev && prev.ok && prev.id != null && !prev.joinUrl) {
        const sdkR = getSdkFn ? getSdkFn() : null
        const svcR = await _calendarService(sdkR)
        const selfR = this
        const callR =
          callFn ||
          (selfR && typeof selfR.call === 'function' ? (name, ...args) => selfR.call(name, ...args) : null)
        const meet = await _ensureMeetRoom({
          id: prev.id,
          title,
          description: ev.notes || ev.description || '',
          row: prev.event,
          call: callR,
          svc: svcR
        })
        prev.roomId = meet.roomId
        prev.joinUrl = meet.joinUrl
        if (meet.event) prev.event = meet.event
        if (meet.error) prev.meetError = meet.error
        else delete prev.meetError
      }
      return prev
    }
    // el.call binds `this` to the calling element (smbls methods.js
    // `method.call(this, …)`); the module's fns are injected into the SHELL
    // context, so meet's eager-slice `createRoom` / `meetRoomUrl` resolve
    // from here exactly as they do for the calendar package.
    const self = this
    const call =
      callFn ||
      (self && typeof self.call === 'function' ? (name, ...args) => self.call(name, ...args) : null)

    const start = ev.start instanceof Date ? ev.start : new Date(ev.start)
    if (isNaN(start.getTime())) return { ok: false, error: 'Invalid event start.' }
    const end =
      ev.end != null
        ? ev.end instanceof Date
          ? ev.end
          : new Date(ev.end)
        : new Date(start.getTime() + 60 * 60 * 1000)
    if (isNaN(end.getTime())) return { ok: false, error: 'Invalid event end.' }

    const sdk = getSdkFn ? getSdkFn() : null
    const svc = await _calendarService(sdk)
    // The calendar service exposes FLAT method names (calendarCreateEvent),
    // not createEvent — and its write contract is { payload, organization,
    // workspaceId } with WIRE columns (date + startTime, not an ISO range).
    // The first cut called svc.createEvent with an ISO shape: undefined
    // method → absorbed into "unavailable", so every chained module event
    // silently never landed (caught live 2026-08-06).
    if (!svc || typeof svc.calendarCreateEvent !== 'function') {
      return { ok: false, error: 'Calendar service unavailable.' }
    }

    const pad = (n) => (n < 10 ? '0' : '') + n
    const dateStr =
      start.getFullYear() + '-' + pad(start.getMonth() + 1) + '-' + pad(start.getDate())
    const timeStr = pad(start.getHours()) + ':' + pad(start.getMinutes())
    const endTimeStr = pad(end.getHours()) + ':' + pad(end.getMinutes())

    try {
      const rec = await svc.calendarCreateEvent({
        payload: {
          // WIRE columns are snake_case (store EVENT_CLIENT_COLUMNS) —
          // camelCase startTime/endTime are silently dropped by the
          // allowlist. start_at/end_at ISO keep windowed list queries
          // working (they filter on startAt).
          title: title,
          date: dateStr,
          start_time: ev.allDay ? null : timeStr,
          end_time: ev.allDay ? null : endTimeStr,
          start_at: start.toISOString(),
          end_at: end.toISOString(),
          type: 'meeting',
          description: ev.notes || ev.description || '',
          // Provenance + module-side fields ride the module-writable bag —
          // `location` is not a wire column and `metadata` is server-owned.
          // `meta` is the module's own record fields (kind, userId,
          // companyId …) — merged UNDER the provenance keys so a module can
          // never spoof source/module/ref, and a chained event can still be
          // reconciled back to the row that minted it
          // (BF-CALENDAR-NATIVE-PROPAGATE-DEAL-CHAIN-1).
          integration_metadata: {
            ...(ev.meta && typeof ev.meta === 'object' && !Array.isArray(ev.meta) ? ev.meta : {}),
            source: 'workspaceModule',
            module: (cap && cap.module) || 'workspaceModule',
            location: ev.location || '',
            ref: ev.ref || ''
          }
        },
        organization: orgId(),
        workspaceId: wsId()
      })
      const row = Array.isArray(rec) ? rec[0] : rec
      const id = row && (row.id || row._id)
      const result = { ok: true, id: id, event: row, roomId: null, joinUrl: null }
      if (wantsMeet && id != null) {
        const meet = await _ensureMeetRoom({ id, title, description: ev.notes || ev.description || '', row, call, svc })
        result.roomId = meet.roomId
        result.joinUrl = meet.joinUrl
        if (meet.event) result.event = meet.event
        if (meet.error) result.meetError = meet.error
      }
      if (refKey) _byRef.set(refKey, result)
      return result
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Could not create the event.' }
    }
  }

  // The room half of a symbols-meet event — find-or-create, exactly as the
  // home "Coming up" Join (eventMeetJoin.js) and New Booking do: a room
  // already keyed by this calendarEventId on the shell's rooms list is
  // reused (a retry never mints a second room), otherwise meet's own
  // `createRoom` writes it. Then the /meet#room= link is appended to the
  // event description through the same calendar service PATCH.
  const _ensureMeetRoom = async ({ id, title, description, row, call, svc }) => {
    if (!call) return { roomId: null, joinUrl: null, error: 'Meet is unavailable in this context.' }
    let roomId = null
    try {
      const rooms = (root && Array.isArray(root.rooms) && root.rooms) || []
      const existing = rooms.find((r) => r && String(r.calendarEventId) === String(id))
      roomId = existing ? existing.id : null
      if (!roomId) {
        roomId = await call('createRoom', {
          name: title || 'Calendar call',
          kind: 'scheduled',
          calendarEventId: id,
          transcribeEnabled: true
        })
      }
    } catch (e) {
      return { roomId: null, joinUrl: null, error: (e && e.message) || 'Could not create the meet room.' }
    }
    if (!roomId) return { roomId: null, joinUrl: null, error: 'Could not create the meet room.' }
    const joinUrl = _meetLinkFor(root, roomId, call)
    if (!joinUrl) return { roomId, joinUrl: null, error: 'Could not build the meet link.' }
    const desc = (row && row.description) || description || ''
    if (desc.indexOf(joinUrl) !== -1) return { roomId, joinUrl }
    const updatedDesc = desc ? desc + '\n' + joinUrl : joinUrl
    let event = row
    if (svc && typeof svc.calendarUpdateEvent === 'function') {
      try {
        const rec = await svc.calendarUpdateEvent({
          id,
          payload: { description: updatedDesc },
          organization: orgId(),
          workspaceId: wsId()
        })
        const patched = Array.isArray(rec) ? rec[0] : rec
        event = patched && (patched.id || patched._id) ? patched : { ...(row || {}), description: updatedDesc }
      } catch (e) {
        return { roomId, joinUrl, error: (e && e.message) || 'Could not stamp the meet link on the event.' }
      }
    }
    return { roomId, joinUrl, event }
  }

  // ── update / remove ───────────────────────────────────────────────────
  // WS-MODULE-CALENDAR-BRIDGE-UPDATE-REMOVE-1. The create half alone made the
  // two stores DRIFT: a module row that moved kept its native twin on the old
  // slot, and a deleted row left an orphan event (plus a live meet room) that
  // no module screen could reach again. Same grant, same service, same wire
  // columns as create — never a second write path.

  const _pad = (n) => (n < 10 ? '0' : '') + n
  const _dateStr = (d) => d.getFullYear() + '-' + _pad(d.getMonth() + 1) + '-' + _pad(d.getDate())
  const _timeStr = (d) => _pad(d.getHours()) + ':' + _pad(d.getMinutes())
  const _asDate = (v) => (v instanceof Date ? v : new Date(v))
  // `this` is the calling element (smbls methods.js binds it) — the same
  // resolution create uses, so meet's fns reach us through el.call.
  const _callOf = (self) =>
    callFn ||
    (self && typeof self.call === 'function' ? (name, ...args) => self.call(name, ...args) : null)

  // Move / rename / re-slot the native twin of a module row.
  //   moduleCalendarUpdate({ id, title?, start?, end?, allDay?, location?,
  //                          notes?, meta? })
  // PARTIAL by design: only the fields the module sent are written, so an
  // edit form that knows about a title never blanks a description. `start`
  // rewrites the whole slot (date + start_time/end_time + start_at/end_at),
  // mirroring create — a half-written slot is how a moved event ends up on
  // two days at once.
  const moduleCalendarUpdate = async function (input = {}) {
    const ev = input || {}
    const id = ev.id != null && String(ev.id) !== '' ? ev.id : null
    if (id == null) return { ok: false, error: 'An event id is required.' }

    const payload = {}
    if (ev.title !== undefined) {
      const title = typeof ev.title === 'string' ? ev.title.trim() : ''
      if (!title) return { ok: false, error: 'An event title cannot be empty.' }
      payload.title = title
    }
    if (ev.notes !== undefined || ev.description !== undefined) {
      payload.description = ev.notes !== undefined ? ev.notes || '' : ev.description || ''
    }
    if (ev.start !== undefined && ev.start !== null) {
      const start = _asDate(ev.start)
      if (isNaN(start.getTime())) return { ok: false, error: 'Invalid event start.' }
      const end =
        ev.end != null ? _asDate(ev.end) : new Date(start.getTime() + 60 * 60 * 1000)
      if (isNaN(end.getTime())) return { ok: false, error: 'Invalid event end.' }
      payload.date = _dateStr(start)
      payload.start_time = ev.allDay ? null : _timeStr(start)
      payload.end_time = ev.allDay ? null : _timeStr(end)
      payload.start_at = start.toISOString()
      payload.end_at = end.toISOString()
    } else if (ev.end !== undefined && ev.end !== null) {
      const end = _asDate(ev.end)
      if (isNaN(end.getTime())) return { ok: false, error: 'Invalid event end.' }
      payload.end_time = ev.allDay ? null : _timeStr(end)
      payload.end_at = end.toISOString()
    } else if (ev.allDay === true) {
      payload.start_time = null
      payload.end_time = null
    }

    const sdk = getSdkFn ? getSdkFn() : null
    const svc = await _calendarService(sdk)
    if (!svc || typeof svc.calendarUpdateEvent !== 'function') {
      return { ok: false, error: 'Calendar service unavailable.' }
    }

    // integration_metadata is a WHOLE-BAG column: patching it with only the
    // new keys would drop `source` / `module` / `ref` and orphan the event
    // from the row that minted it. So read the live bag first and merge —
    // module `meta` UNDER the provenance keys, exactly as create does.
    const wantsMeta =
      ev.location !== undefined ||
      ev.ref !== undefined ||
      (ev.meta && typeof ev.meta === 'object' && !Array.isArray(ev.meta))
    if (wantsMeta) {
      let current = null
      if (typeof svc.calendarGetEvent === 'function') {
        try {
          const got = await svc.calendarGetEvent({ id, workspaceId: wsId() })
          current = Array.isArray(got) ? got[0] : got
        } catch (_) {
          current = null
        }
      }
      const prev = (current && current.integration_metadata) || {}
      payload.integration_metadata = {
        ...prev,
        ...(ev.meta && typeof ev.meta === 'object' && !Array.isArray(ev.meta) ? ev.meta : {}),
        source: 'workspaceModule',
        module: (cap && cap.module) || 'workspaceModule',
        location: ev.location !== undefined ? ev.location || '' : prev.location || '',
        ref: ev.ref !== undefined ? ev.ref || '' : prev.ref || ''
      }
    }

    try {
      const rec = await svc.calendarUpdateEvent({
        id,
        payload,
        organization: orgId(),
        workspaceId: wsId()
      })
      const row = Array.isArray(rec) ? rec[0] : rec
      return { ok: true, id: (row && (row.id || row._id)) != null ? row.id || row._id : id, event: row }
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Could not update the event.' }
    }
  }

  // Retire the native twin. Soft-delete through the service (the calendar
  // app's own `deleteEvent` flips `deleted` so the sync server can push a
  // tombstone — the service DELETE route is that same soft-delete), then end
  // the meet room keyed by this event through MEET'S OWN `endRoom` (el.call),
  // never a second meet_rooms write. The event delete comes FIRST: if it is
  // refused, the room must stay live.
  const moduleCalendarRemove = async function (input = {}) {
    const ev = input || {}
    const id = ev.id != null && String(ev.id) !== '' ? ev.id : null
    if (id == null) return { ok: false, error: 'An event id is required.' }

    const sdk = getSdkFn ? getSdkFn() : null
    const svc = await _calendarService(sdk)
    if (!svc || typeof svc.calendarDeleteEvent !== 'function') {
      return { ok: false, error: 'Calendar service unavailable.' }
    }
    try {
      await svc.calendarDeleteEvent({ id, organization: orgId(), workspaceId: wsId() })
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Could not remove the event.' }
    }

    const result = { ok: true, id, roomId: null, roomEnded: false }
    const rooms = (root && Array.isArray(root.rooms) && root.rooms) || []
    const room = rooms.find((r) => r && String(r.calendarEventId) === String(id))
    if (!room) return result
    result.roomId = room.id
    const call = _callOf(this)
    // `el.call` on an UNREGISTERED name is a silent no-op that returns
    // undefined — so an absent `endRoom` (it was missing from meet's eager
    // slice until this ticket) would otherwise be reported as a successful
    // end while the room stayed live. Assert the registration, and treat only
    // an explicit `true` as ended.
    const bag = (root && root.context && root.context.functions) || null
    if (!call || (bag && typeof bag.endRoom !== 'function')) {
      result.meetError = 'Meet is unavailable in this context.'
      return result
    }
    try {
      const ended = await call('endRoom', room.id)
      result.roomEnded = ended === true
      if (!result.roomEnded) result.meetError = 'Could not end the meet room.'
    } catch (e) {
      result.meetError = (e && e.message) || 'Could not end the meet room.'
    }
    return result
  }

  // Read the workspace's events back, so a module can render the NATIVE
  // calendar rather than its own private copy.
  const moduleCalendarList = async function (range = {}) {
    const sdk = getSdkFn ? getSdkFn() : null
    const svc = await _calendarService(sdk)
    if (!svc || typeof svc.calendarListEvents !== 'function') {
      return { ok: false, error: 'Calendar service unavailable.', events: [] }
    }
    try {
      const q = { workspaceId: wsId(), organization: orgId() }
      if (range && (range.from || range.to)) {
        q.window = {}
        if (range.from) q.window.gte = new Date(range.from).toISOString()
        if (range.to) q.window.lte = new Date(range.to).toISOString()
      }
      const res = await svc.calendarListEvents(q)
      const events = Array.isArray(res) ? res : (res && (res.data || res.events)) || []
      return { ok: true, events: events }
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Could not load events.', events: [] }
    }
  }

  return { moduleCalendarCreate, moduleCalendarUpdate, moduleCalendarRemove, moduleCalendarList }
}
